/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_ITEM_DETAIL_REDESIGN
 * Date:         2026-02-10
 * Modified By:  Claude
 *
 * Description:
 * Redesigned to match inventory-item-detail.html mockup:
 * - Added hero banner with title + meta pill tags
 * - Changed grid from 1fr 1fr to 1fr 340px with 24px gap
 * - Moved RecentBroadcasts inside left column (below ItemInformation)
 * - Added sticky positioning for CompanyInformation sidebar
 *
 * Related Screens / Modules:
 * - client/src/pages/ItemDetailPage/components/
 * - client/src/services/inventoryService.ts
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner, Alert, Button } from "react-bootstrap";
import { getInventoryItem } from "../../services/inventoryService";
import { ItemDetail, SellerInfo } from "./interfaces/itemDetail";
import { notify } from "../../utils/notifications";
import ItemInformation from "./components/ItemInformation";
import CompanyInformation from "./components/CompanyInformation";
import RecentBroadcasts from "./components/RecentBroadcasts";
import "./ItemDetailPage.scss";

// [2025-01-12] Item detail: Main page component with three-section layout

const ItemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // [2025-01-12] Fetch item data on mount
  useEffect(() => {
    const fetchItem = async () => {
      if (!id) {
        setError("Item ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getInventoryItem(id);
        const inventoryData = response.data;

        // [2025-01-12] Transform Inventory to ItemDetail format
        // Handle sellerId - it can be a string or populated object
        let sellerIdValue: SellerInfo | string = inventoryData.sellerId;
        if (typeof inventoryData.sellerId === "object" && inventoryData.sellerId !== null) {
          // Already populated, use as-is
          sellerIdValue = inventoryData.sellerId as SellerInfo;
        } else if (inventoryData.seller) {
          // Use seller field if available (legacy populate)
          sellerIdValue = {
            _id: typeof inventoryData.sellerId === "string" ? inventoryData.sellerId : "",
            firstName: inventoryData.seller.firstName,
            lastName: inventoryData.seller.lastName,
            companyName: inventoryData.seller.companyName,
          };
        }

        const itemDetail: ItemDetail = {
          _id: inventoryData._id || "",
          title: inventoryData.title,
          manufacturer: inventoryData.manufacturer,
          model: inventoryData.model,
          partNumber: inventoryData.partNumber,
          category: inventoryData.category,
          condition: inventoryData.condition,
          quantity: inventoryData.quantity,
          price: inventoryData.price,
          currency: inventoryData.currency,
          country: inventoryData.country,
          description: inventoryData.description,
          sellerId: sellerIdValue,
          isActive: inventoryData.isActive,
          createdAt: inventoryData.createdAt,
          updatedAt: inventoryData.updatedAt,
        };

        setItem(itemDetail);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || "We couldn't load this item. Please refresh the page";
        setError(errorMessage);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(err, "We couldn't load this item. Please refresh the page");
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [id]);

  // [2025-12-13] Back button: Navigate to previous page
  const handleBack = () => {
    navigate(-1); // Navigate to previous page
  };

  // [2026-02-10] Loading state
  if (loading) {
    return (
      <div className="item-detail-page">
        <div className="item-detail-page__back">
          <button className="btn-back" onClick={handleBack}>
            ← Back
          </button>
        </div>
        <div className="item-detail-page__loading">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  // [2026-02-10] Error state
  if (error || !item) {
    return (
      <div className="item-detail-page">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error || "Item not found"}</p>
          <Button variant="outline-danger" onClick={() => navigate("/inventory")}>
            Back to Inventory
          </Button>
        </Alert>
      </div>
    );
  }

  // [2025-01-12] Extract seller information
  const seller: SellerInfo =
    typeof item.sellerId === "object" && item.sellerId !== null
      ? (item.sellerId as SellerInfo)
      : {
          _id: typeof item.sellerId === "string" ? item.sellerId : "",
          firstName: "",
          lastName: "",
        };

  const companyName =
    seller.companyName || `${seller.firstName} ${seller.lastName}`.trim() || "Company";
  const sellerId = seller._id || (typeof item.sellerId === "string" ? item.sellerId : "");

  // [2026-02-10] Format price with currency symbol for hero meta
  const formatPrice = (price?: number, currency?: string) => {
    if (price === undefined || price === null) return null;
    const sym = currency === "EUR" ? "€" : currency === "GBP" ? "£" : "$";
    return `${sym}${Math.round(price).toLocaleString("en-US")}`;
  };

  // [2026-02-10] Get condition badge class for hero meta
  const getConditionClass = (condition?: string) => {
    if (!condition) return "";
    const normalized = condition.toLowerCase().trim();
    if (normalized.includes("new")) return "badge-condition--new";
    if (normalized.includes("used")) return "badge-condition--used";
    if (normalized.includes("refurbished")) return "badge-condition--refurbished";
    return "";
  };

  return (
    <div className="item-detail-page">
      {/* [2026-02-10] Back button */}
      <div className="item-detail-page__back">
        <button className="btn-back" onClick={handleBack}>
          ← Back
        </button>
      </div>

      {/* [2026-02-10] Hero Banner with title + meta tags */}
      <div className="item-detail-page__hero">
        <h1 className="item-detail-page__hero-title">{item.title}</h1>
        <div className="item-detail-page__hero-meta">
          {item.manufacturer && (
            <span className="item-detail-page__hero-tag">
              <strong>{item.manufacturer}</strong>
            </span>
          )}
          {item.partNumber && (
            <span className="item-detail-page__hero-tag">
              SKU: <strong>{item.partNumber}</strong>
            </span>
          )}
          {item.condition && (
            <span className={`badge-condition ${getConditionClass(item.condition)}`}>
              {item.condition}
            </span>
          )}
          <span className="item-detail-page__hero-tag">
            QTY: <strong>{item.quantity}</strong>
          </span>
          {item.price && (
            <span className="item-detail-page__hero-tag item-detail-page__hero-tag--price">
              {formatPrice(item.price, item.currency)}
            </span>
          )}
        </div>
      </div>

      {/* [2026-02-10] Body Grid: 1fr 340px layout */}
      <div className="item-detail-page__body">
        {/* Left Column: Item Information + Recent Broadcasts */}
        <div className="item-detail-page__main">
          <ItemInformation item={item} />
          {sellerId && (
            <div className="item-detail-page__broadcasts">
              <RecentBroadcasts sellerId={sellerId} companyName={companyName} />
            </div>
          )}
        </div>

        {/* Right Column: Company Information (sticky) */}
        {sellerId && (
          <div className="item-detail-page__sidebar">
            <CompanyInformation seller={seller} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemDetailPage;
